import { useNavigate, NavigateOptions, To } from 'react-router-dom';
import { analytics } from '@/lib/analytics';
import { useEffect, useRef } from 'react';

/**
 * Enhanced navigate hook that automatically tracks navigation events
 * Drop-in replacement for react-router-dom's useNavigate
 */
export function useTrackedNavigate() {
  const navigate = useNavigate();
  const previousPath = useRef<string>(window.location.pathname);

  useEffect(() => {
    // Track initial page view
    analytics.trackPageView();
    
    // Update previous path
    previousPath.current = window.location.pathname;
  }, []);

  const trackedNavigate = (to: To | number, options?: NavigateOptions) => {
    const origin = previousPath.current;
    
    // Handle numeric navigation (back/forward)
    if (typeof to === 'number') {
      analytics.trackAction('navigation_back_forward', {
        delta: to,
        origin,
      });
      navigate(to);
      return;
    }

    // Convert To type to string for tracking
    const destination = typeof to === 'string' ? to : to.pathname || '';

    // Track navigation event
    analytics.trackAction('navigation', {
      from: origin,
      to: destination,
      ...options,
    });

    // Perform navigation
    navigate(to, options);

    // Track new page view after navigation
    setTimeout(() => {
      analytics.trackPageView(destination);
      previousPath.current = destination;
    }, 100);
  };

  return trackedNavigate;
}
